// Lightweight SEO helper for SPA routes: inject/update meta, canonical, favicon, and JSON-LD
// Usage: const dispose = applySeo({...}); on unmount, call dispose() to remove injected tags.

export type SeoInput = {
  title?: string;
  description?: string;
  keywords?: string[] | string;
  image?: string;
  twitterImage?: string; // optional square image for Twitter/X
  imageAlt?: string;
  url?: string;
  siteName?: string;
  twitterHandle?: string; // e.g., "@yourbrand"
  canonicalUrl?: string;
  faviconUrl?: string;
  /** PNG for iOS/Android Add to Home Screen (defaults applied in SiteBrandingProvider). */
  appleTouchIconUrl?: string;
  noIndex?: boolean; // if true, set robots noindex,nofollow
  jsonLd?: Record<string, any> | Array<Record<string, any>>;
};

const FLAG = 'data-spa-seo';

function ensureHead(): HTMLHeadElement {
  return document.head || document.getElementsByTagName('head')[0];
}

function setOrCreate<K extends HTMLElement>(selector: string, create: () => K): K {
  const head = ensureHead();
  let el = head.querySelector(selector) as K | null;
  if (!el) {
    el = create();
    (el as any).setAttribute(FLAG, '1');
    head.appendChild(el);
  }
  return el;
}

function setMetaName(name: string, content: string | undefined | null) {
  if (!content) return null;
  const head = ensureHead();
  let el = head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  const created = !el;
  const prev = el?.getAttribute('content') ?? '';
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    head.appendChild(el);
  }
  el.setAttribute(FLAG, '1');
  el.content = content;
  (el as any).__seoPrev = created ? null : prev;
  (el as any).__seoCreated = created;
  return el;
}

function setMetaProp(property: string, content: string | undefined | null) {
  if (!content) return null;
  const head = ensureHead();
  let el = head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  const created = !el;
  const prev = el?.getAttribute('content') ?? '';
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    head.appendChild(el);
  }
  el.setAttribute(FLAG, '1');
  el.content = content;
  (el as any).__seoPrev = created ? null : prev;
  (el as any).__seoCreated = created;
  return el;
}

function disposeMetaEl(el: HTMLMetaElement | null) {
  if (!el) return;
  if ((el as any).__seoCreated) {
    el.remove();
    return;
  }
  const prev = (el as any).__seoPrev;
  if (prev != null && prev !== '') el.content = prev;
  else el.removeAttribute('content');
  el.removeAttribute(FLAG);
}

function setLinkRel(rel: string, href: string | undefined | null, type?: string) {
  if (!href) return null;
  const el = setOrCreate<HTMLLinkElement>(`link[${FLAG}][rel="${rel}"]`, () => {
    const l = document.createElement('link');
    l.setAttribute('rel', rel);
    return l;
  });
  el.href = href;
  if (type) el.type = type;
  else el.removeAttribute('type');
  return el;
}

/** Tab favicon (SVG) + home-screen icon (PNG raster from favicon.svg). */
function setFaviconLinks(tabIconHref: string, appleTouchHref?: string) {
  if (!tabIconHref) return [];

  try {
    document
      .querySelectorAll('link[rel="icon"][href*="/favicon.ico"]')
      .forEach((el) => {
        if (!el.hasAttribute(FLAG)) el.remove();
      });
  } catch {
    /* ignore */
  }

  const isSvg = /\.svg($|\?)/i.test(tabIconHref);
  const icon = setLinkRel('icon', tabIconHref, isSvg ? 'image/svg+xml' : undefined);
  const apple = appleTouchHref ? setLinkRel('apple-touch-icon', appleTouchHref, 'image/png') : null;
  return [icon, apple].filter(Boolean) as HTMLLinkElement[];
}

function setJsonLd(obj: any) {
  if (!obj) return null;
  const el = setOrCreate<HTMLScriptElement>(`script[${FLAG}][type="application/ld+json"]`, () => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    return s;
  });
  try {
    el.text = JSON.stringify(obj);
  } catch {
    // no-op
  }
  return el;
}

function toArrayKeywords(v?: string | string[]) {
  if (!v) return [] as string[];
  if (Array.isArray(v)) return v;
  return String(v).split(',').map(s => s.trim()).filter(Boolean);
}

export function applySeo(input: SeoInput) {
  const disposers: Array<() => void> = [];

  // Title
  if (input.title) {
    const prevTitle = document.title;
    document.title = input.title;
    disposers.push(() => { document.title = prevTitle; });
  }

  // Canonical URL
  const canonicalUrl = input.canonicalUrl || input.url || (typeof window !== 'undefined' ? window.location.href : undefined);
  const canonicalEl = setLinkRel('canonical', canonicalUrl || '');
  if (canonicalEl) disposers.push(() => canonicalEl.remove());

  // Description / keywords
  const descEl = setMetaName('description', input.description || '');
  if (descEl) disposers.push(() => disposeMetaEl(descEl));
  const keywords = toArrayKeywords(input.keywords);
  const kwEl = keywords.length ? setMetaName('keywords', keywords.join(', ')) : null;
  if (kwEl) disposers.push(() => disposeMetaEl(kwEl));

  // Open Graph
  const ogTitle = setMetaProp('og:title', input.title || '');
  if (ogTitle) disposers.push(() => disposeMetaEl(ogTitle));
  const ogDesc = setMetaProp('og:description', input.description || '');
  if (ogDesc) disposers.push(() => disposeMetaEl(ogDesc));
  const ogImage = input.image ? setMetaProp('og:image', input.image) : null;
  if (ogImage) disposers.push(() => disposeMetaEl(ogImage));
  const ogImageAlt = input.imageAlt ? setMetaProp('og:image:alt', input.imageAlt) : null;
  if (ogImageAlt) disposers.push(() => disposeMetaEl(ogImageAlt));
  const ogUrl = canonicalUrl ? setMetaProp('og:url', canonicalUrl) : null;
  if (ogUrl) disposers.push(() => disposeMetaEl(ogUrl));
  const ogSite = input.siteName ? setMetaProp('og:site_name', input.siteName) : null;
  if (ogSite) disposers.push(() => disposeMetaEl(ogSite));
  if (input.image) {
    const ogW = setMetaProp('og:image:width', '1200');
    if (ogW) disposers.push(() => disposeMetaEl(ogW));
    const ogH = setMetaProp('og:image:height', '630');
    if (ogH) disposers.push(() => disposeMetaEl(ogH));
  }

  // Twitter
  const twCard = setMetaName('twitter:card', 'summary_large_image');
  if (twCard) disposers.push(() => disposeMetaEl(twCard));
  const twSite = input.twitterHandle ? setMetaName('twitter:site', input.twitterHandle) : null;
  if (twSite) disposers.push(() => disposeMetaEl(twSite));
  const twTitle = setMetaName('twitter:title', input.title || '');
  if (twTitle) disposers.push(() => disposeMetaEl(twTitle));
  const twDesc = setMetaName('twitter:description', input.description || '');
  if (twDesc) disposers.push(() => disposeMetaEl(twDesc));
  const twImageUrl = (input as any).twitterImage || input.image;
  const twImage = twImageUrl ? setMetaName('twitter:image', twImageUrl) : null;
  if (twImage) disposers.push(() => disposeMetaEl(twImage));
  const twImageAlt = input.imageAlt ? setMetaName('twitter:image:alt', input.imageAlt) : null;
  if (twImageAlt) disposers.push(() => disposeMetaEl(twImageAlt));

  // Favicon — always SVG canonical path when provided
  const favEls = input.faviconUrl
    ? setFaviconLinks(
        input.faviconUrl,
        input.appleTouchIconUrl ||
          (/\.svg($|\?)/i.test(input.faviconUrl) ? '/apple-touch-icon.png' : input.faviconUrl)
      )
    : [];
  favEls.forEach((el) => disposers.push(() => el.remove()));

  // Robots (optional)
  const robots = setMetaName('robots', input.noIndex ? 'noindex, nofollow' : 'index, follow');
  if (robots) disposers.push(() => robots.remove());

  // JSON-LD (optional)
  const jsonLdEl = input.jsonLd ? setJsonLd(input.jsonLd) : null;
  if (jsonLdEl) disposers.push(() => jsonLdEl.remove());

  return () => {
    disposers.splice(0).forEach(fn => {
      try { fn(); } catch {}
    });
  };
}
